import { EditIcon, SearchIcon } from '@chakra-ui/icons';
import {
  Box,
  Flex,
  IconButton,
  Input,
  InputGroup,
  InputLeftElement,
  Text,
  VStack,
} from '@chakra-ui/react';
import { useMemo, useState } from 'react';

interface Conversation {
  id: string;
  title: string;
  updatedAt: string;
}

interface ConversationHistoryPanelProps {
  isOpen: boolean;
  onClose: () => void;
  conversations: Conversation[];
  currentConversationId?: string;
  onSelectConversation: (id: string) => void;
  onNewChat: () => void;
}

export function ConversationHistoryPanel({
  isOpen,
  onClose,
  conversations,
  currentConversationId,
  onSelectConversation,
  onNewChat,
}: ConversationHistoryPanelProps) {
  const [searchQuery, setSearchQuery] = useState('');

  const filteredConversations = useMemo(
    () =>
      searchQuery.trim()
        ? conversations.filter((conv) =>
            conv.title.toLowerCase().includes(searchQuery.toLowerCase())
          )
        : conversations,
    [conversations, searchQuery]
  );

  return (
    <Flex
      position="absolute"
      top={0}
      left={0}
      height="100%"
      width="100%"
      zIndex={10}
      transform={isOpen ? 'translateX(0)' : 'translateX(-100%)'}
      transition="transform 0.3s ease-in-out"
      overflow="hidden"
    >
      {/* History panel - 80% width */}
      <Flex
        width="80%"
        height="100%"
        bg="white"
        direction="column"
        overflow="hidden"
      >
        <Flex p={2} gap={2} align="center" flexShrink={0}>
          <InputGroup size="sm">
            <InputLeftElement pointerEvents="none">
              <SearchIcon color="gray.400" boxSize={4} />
            </InputLeftElement>
            <Input
              placeholder="Search"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              borderRadius="md"
            />
          </InputGroup>
          <IconButton
            aria-label="New chat"
            icon={<EditIcon />}
            size="sm"
            variant="ghost"
            colorScheme="gray"
            onClick={() => {
              onNewChat();
              onClose();
            }}
          />
        </Flex>
        <VStack align="stretch" spacing={2} flex={1} overflowY="auto" p={1}>
          {filteredConversations.length === 0 ? (
            <Box py={4} textAlign="center">
              <Text fontSize="sm" color="gray.500">
                {searchQuery.trim()
                  ? 'No conversations match your search'
                  : 'No saved conversations'}
              </Text>
            </Box>
          ) : (
            filteredConversations.map((conv) => (
              <Box
                key={conv.id}
                as="button"
                type="button"
                textAlign="left"
                w="100%"
                py={2}
                px={2}
                borderRadius="md"
                bg={
                  currentConversationId === conv.id ? 'gray.100' : 'transparent'
                }
                _hover={{
                  bg:
                    currentConversationId === conv.id ? 'gray.100' : 'gray.50',
                }}
                onClick={() => onSelectConversation(conv.id)}
              >
                <Text
                  fontSize="sm"
                  fontWeight="medium"
                  color="gray.800"
                  noOfLines={1}
                >
                  {conv.title}
                </Text>
              </Box>
            ))
          )}
        </VStack>
      </Flex>
      {/* Transparent 20% strip - drawer overlay shows through for single shade; click to close */}
      <Box
        flex={1}
        height="100%"
        bg="transparent"
        cursor="pointer"
        onClick={onClose}
        aria-label="Close conversation history"
        pointerEvents={isOpen ? 'auto' : 'none'}
      />
    </Flex>
  );
}
